import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Table,
  Button,
  Modal,
  Input,
  InputNumber,
  Select,
  Switch,
  message,
  Spin,
} from 'antd';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import AdminLayout from '../../../components/admin/AdminLayout';
import { adminApi } from '../../../modules/api';
import { clearSession } from '../../../modules/auth';
import { refreshSubscriptions } from '../../../modules/subscriptionStorage';
import { formatMonthlyPrice } from '../../../utils/pricing';
import './adminProducts.css';

const arrayMove = (items, fromIndex, toIndex) => {
  const next = items.slice();
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

const SortableDragHandle = SortableHandle(() => (
  <button type="button" className="admin-products__drag-handle" aria-label="Reorder model">
    <i className="fas fa-grip-vertical" />
  </button>
));

class AdminProducts extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      products: [],
      generationModels: [],
      generationModelsLoading: true,
      generationModelsSaving: {},
      generationModelsOrderSaving: false,
      generationModelDrafts: {},
      editing: null,
      modalVisible: false,
      saving: false,
      formValues: {
        description: '',
        features: [],
        tempFeature: '',
        price: 0,
        period: 30,
        periodUnit: 'day',
        generationLimit: 1500,
        isActive: true,
        isPublic: false,
        landingTitle: '',
        landingTagline: '',
        landingDescription: '',
        landingPriceText: '',
        landingBillingText: '',
        landingSaveText: '',
        landingBadge: '',
        landingBestFor: '',
        landingTheme: 'lilac',
        landingButtonLabel: 'Select Plan',
        landingCreditsPerYear: 0,
        landingBonusCredits: 0,
        landingDisplayOrder: 0,
      },
    };
  }

  componentDidMount() {
    this.fetchProducts();
    this.fetchGenerationModels();
  }

  async fetchProducts() {
    this.setState({ loading: true });
    try {
      const response = await adminApi.fetchProducts();
      const data = response && response.data ? response.data : response;
      this.setState({ products: Array.isArray(data) ? data : [], loading: false });
    } catch (error) {
      this.setState({ loading: false });
    }
  }

  buildGenerationModelDrafts(models = []) {
    return models.reduce((accumulator, model) => {
      const modelKey = model.modelKey || model.id;
      if (!modelKey) {
        return accumulator;
      }
      accumulator[modelKey] = {
        label: model.label || '',
        provider: model.provider || '',
        providerModel: model.providerModel || '',
        generationCreditCost:
          model.generationCreditCost != null ? Number(model.generationCreditCost) : 0,
        minimumGenerationCreditCost:
          model.minimumGenerationCreditCost != null ? Number(model.minimumGenerationCreditCost) : 0,
        providerCostUsd:
          model.providerCostUsd != null ? Number(model.providerCostUsd) : 0,
        supportsReferenceImages: Boolean(model.supportsReferenceImages),
        category: model.category || 'generate',
        isEnabled: Boolean(model.isEnabled),
        sortOrder: model.sortOrder != null ? Number(model.sortOrder) : 0,
        description: model.description || '',
      };
      return accumulator;
    }, {});
  }

  sortGenerationModels = (models = []) => (
    Array.isArray(models)
      ? models.slice().sort((left, right) => {
          const leftOrder = Number(left.sortOrder) || 0;
          const rightOrder = Number(right.sortOrder) || 0;
          if (leftOrder !== rightOrder) {
            return leftOrder - rightOrder;
          }
          return String(left.label || '').localeCompare(String(right.label || ''));
        })
      : []
  );

  async fetchGenerationModels() {
    this.setState({ generationModelsLoading: true });
    try {
      const response = await adminApi.fetchGenerationModels();
      const data = response && response.data ? response.data : response;
      const models = this.sortGenerationModels(Array.isArray(data) ? data : []);
      this.setState({
        generationModels: models,
        generationModelDrafts: this.buildGenerationModelDrafts(models),
        generationModelsLoading: false,
      });
    } catch (error) {
      this.setState({
        generationModels: [],
        generationModelDrafts: {},
        generationModelsLoading: false,
      });
    }
  }

  handleGenerationModelsSortEnd = async ({ oldIndex, newIndex }) => {
    const { generationModels, generationModelsOrderSaving } = this.state;
    if (generationModelsOrderSaving || oldIndex === newIndex) {
      return;
    }

    const reordered = arrayMove(generationModels, oldIndex, newIndex).map((model, index) => ({
      ...model,
      sortOrder: (index + 1) * 10,
    }));
    const nextDrafts = reordered.reduce((accumulator, model) => {
      const modelKey = model.modelKey || model.id;
      accumulator[modelKey] = {
        ...(this.state.generationModelDrafts[modelKey] || {}),
        sortOrder: model.sortOrder,
      };
      return accumulator;
    }, {});

    this.setState({
      generationModels: reordered,
      generationModelDrafts: {
        ...this.state.generationModelDrafts,
        ...nextDrafts,
      },
      generationModelsOrderSaving: true,
    });

    try {
      const response = await adminApi.reorderGenerationModels(
        reordered.map((model) => ({
          modelKey: model.modelKey || model.id,
          sortOrder: model.sortOrder,
        })),
      );
      const updated = response && response.data ? response.data : response;
      const models = this.sortGenerationModels(Array.isArray(updated) ? updated : reordered);
      const existingDrafts = this.state.generationModelDrafts;
      const mergedDrafts = models.reduce((accumulator, model) => {
        const modelKey = model.modelKey || model.id;
        accumulator[modelKey] = {
          ...(existingDrafts[modelKey] || {}),
          sortOrder: model.sortOrder,
        };
        return accumulator;
      }, {});
      this.setState({
        generationModels: models,
        generationModelDrafts: {
          ...existingDrafts,
          ...mergedDrafts,
        },
      });
      message.success('Model order updated.');
    } catch (error) {
      message.error('Unable to reorder models.');
      this.fetchGenerationModels();
    } finally {
      this.setState({ generationModelsOrderSaving: false });
    }
  };

  openModal = (product) => {
    const landing = product.landing || {};
    this.setState({
      editing: product,
      modalVisible: true,
      formValues: {
        description: product.description,
        features: Array.isArray(product.features) ? product.features : [],
        tempFeature: '',
        price: product.price,
        period: product.period,
        periodUnit: product.periodUnit || 'day',
        generationLimit: product.generationLimit || 1500,
        isActive: product.isActive,
        isPublic: Boolean(product.isPublic),
        landingTitle: landing.title || '',
        landingTagline: landing.tagline || '',
        landingDescription: landing.description || '',
        landingPriceText: landing.priceText || '',
        landingBillingText: landing.billingText || '',
        landingSaveText: landing.saveText || '',
        landingBadge: landing.badge || '',
        landingBestFor: landing.bestFor || '',
        landingTheme: landing.theme || 'lilac',
        landingButtonLabel: landing.buttonLabel || 'Select Plan',
        landingCreditsPerYear: landing.creditsPerYear || 0,
        landingBonusCredits: landing.bonusCredits || 0,
        landingDisplayOrder: landing.displayOrder || 0,
      },
    });
  };

  closeModal = () => {
    this.setState({
      modalVisible: false,
      editing: null,
      saving: false,
      formValues: {
        description: '',
        features: [],
        tempFeature: '',
        price: 0,
        period: 30,
        periodUnit: 'day',
        generationLimit: 1500,
        isActive: true,
        isPublic: false,
        landingTitle: '',
        landingTagline: '',
        landingDescription: '',
        landingPriceText: '',
        landingBillingText: '',
        landingSaveText: '',
        landingBadge: '',
        landingBestFor: '',
        landingTheme: 'lilac',
        landingButtonLabel: 'Select Plan',
        landingCreditsPerYear: 0,
        landingBonusCredits: 0,
        landingDisplayOrder: 0,
      },
    });
  };

  openCreateModal = () => {
    this.setState({
      editing: null,
      modalVisible: true,
      formValues: {
        description: '',
        features: [],
        tempFeature: '',
        price: 0,
        period: 30,
        periodUnit: 'day',
        generationLimit: 1500,
        isActive: true,
        isPublic: false,
        landingTitle: '',
        landingTagline: '',
        landingDescription: '',
        landingPriceText: '',
        landingBillingText: '',
        landingSaveText: '',
        landingBadge: '',
        landingBestFor: '',
        landingTheme: 'lilac',
        landingButtonLabel: 'Select Plan',
        landingCreditsPerYear: 0,
        landingBonusCredits: 0,
        landingDisplayOrder: 0,
      },
    });
  };

  handleInputChange = (field, value) => {
    this.setState((prevState) => ({
      formValues: {
        ...prevState.formValues,
        [field]: value,
      },
    }));
  };

  handleGenerationModelInputChange = (modelKey, field, value) => {
    this.setState((prevState) => ({
      generationModelDrafts: {
        ...prevState.generationModelDrafts,
        [modelKey]: {
          ...(prevState.generationModelDrafts[modelKey] || {}),
          [field]: value,
        },
      },
    }));
  };

  handleSaveGenerationModel = async (modelKey) => {
    const { generationModelDrafts } = this.state;
    const draft = generationModelDrafts[modelKey];
    if (!draft) {
      message.warning('Unable to locate the model settings.');
      return;
    }

    const minimumCreditCost = Number(draft.minimumGenerationCreditCost) || 0;
    const requestedCreditCost = Number(draft.generationCreditCost) || 0;
    if (minimumCreditCost > 0 && requestedCreditCost < minimumCreditCost) {
      message.error(`The minimum credit allowed is ${minimumCreditCost}.`);
      return;
    }

    this.setState((prevState) => ({
      generationModelsSaving: {
        ...prevState.generationModelsSaving,
        [modelKey]: true,
      },
    }));

    try {
      const payload = {
        label: (draft.label || '').trim(),
        provider: (draft.provider || '').trim(),
        providerModel: (draft.providerModel || '').trim(),
        generationCreditCost: Number(draft.generationCreditCost) || 0,
        providerCostUsd: Number(draft.providerCostUsd) || 0,
        supportsReferenceImages: Boolean(draft.supportsReferenceImages),
        category: draft.category || 'generate',
        isEnabled: Boolean(draft.isEnabled),
        sortOrder: Number(draft.sortOrder) || 0,
        description: (draft.description || '').trim(),
      };
      const response = await adminApi.updateGenerationModel(modelKey, payload);
      const updated = response && response.data ? response.data : response;
      const nextModels = this.sortGenerationModels(
        this.state.generationModels.map((item) => (
          (item.modelKey || item.id) === modelKey ? updated : item
        )),
      );
      this.setState((prevState) => ({
        generationModels: nextModels,
        generationModelDrafts: {
          ...prevState.generationModelDrafts,
          [modelKey]: {
            ...draft,
            ...updated,
            generationCreditCost: Number(updated.generationCreditCost) || 0,
            providerCostUsd: Number(updated.providerCostUsd) || 0,
            supportsReferenceImages: Boolean(updated.supportsReferenceImages),
            isEnabled: Boolean(updated.isEnabled),
            sortOrder: Number(updated.sortOrder) || 0,
          },
        },
      }));
      message.success(`Updated ${updated.label || modelKey}.`);
    } catch (error) {
      const errMessage = (error && error.response && error.response.data && error.response.data.message)
        ? error.response.data.message
        : 'Unable to update the model settings.';
      message.error(errMessage);
    } finally {
      this.setState((prevState) => ({
        generationModelsSaving: {
          ...prevState.generationModelsSaving,
          [modelKey]: false,
        },
      }));
    }
  };

  renderGenerationModelsTable() {
    const {
      generationModels,
      generationModelDrafts,
      generationModelsLoading,
      generationModelsOrderSaving,
    } = this.state;

    const SortableRow = SortableElement((rowProps) => <tr {...rowProps} />);
    const SortableBody = SortableContainer((bodyProps) => <tbody {...bodyProps} />);

    const columns = [
      {
        title: '',
        key: 'drag',
        width: 48,
        render: () => <SortableDragHandle />,
      },
      {
        title: 'Model',
        key: 'model',
        width: 360,
        render: (_, record) => {
          const current = generationModelDrafts[record.modelKey] || {};
          const referenceSupport = current.supportsReferenceImages != null
            ? current.supportsReferenceImages
            : record.supportsReferenceImages;
          return (
            <div className="admin-products__model-meta-block">
              <strong className="admin-products__model-title">{current.label || record.label}</strong>
              <span className="admin-products__model-meta">
                Reference images: {referenceSupport ? 'Yes' : 'No'}
              </span>
            </div>
          );
        },
      },
      {
        title: 'Credit cost / image',
        key: 'generationCreditCost',
        width: 180,
        render: (_, record) => {
          const current = generationModelDrafts[record.modelKey] || {};
          const creditCost = current.generationCreditCost != null ? current.generationCreditCost : record.generationCreditCost;
          const minimumCreditCost = Number(current.minimumGenerationCreditCost || record.minimumGenerationCreditCost || 0);
          const isBelowMinimum = minimumCreditCost > 0 && Number(creditCost) < minimumCreditCost;
          return (
            <div className="admin-products__model-field">
              <InputNumber
                min={minimumCreditCost > 0 ? minimumCreditCost : 0}
                precision={0}
                status={isBelowMinimum ? 'error' : undefined}
                style={{ width: '100%' }}
                value={creditCost}
                onChange={(value) => this.handleGenerationModelInputChange(record.modelKey, 'generationCreditCost', value)}
              />
              {minimumCreditCost > 0 && (
                <span className={`admin-products__model-floor${isBelowMinimum ? ' is-error' : ''}`}>
                  Minimum allowed: {minimumCreditCost} credit{minimumCreditCost === 1 ? '' : 's'}
                </span>
              )}
            </div>
          );
        },
      },
      {
        title: 'Enabled',
        key: 'isEnabled',
        width: 110,
        align: 'center',
        render: (_, record) => {
          const current = generationModelDrafts[record.modelKey] || {};
          const enabled = current.isEnabled != null ? current.isEnabled : record.isEnabled;
          return (
            <Switch
              checked={enabled}
              onChange={(value) => this.handleGenerationModelInputChange(record.modelKey, 'isEnabled', value)}
            />
          );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 120,
        render: (_, record) => {
          const modelKey = record.modelKey || record.id;
          const isSaving = Boolean(this.state.generationModelsSaving[modelKey]);
          const current = generationModelDrafts[modelKey] || {};
          const minimumCreditCost = Number(current.minimumGenerationCreditCost || record.minimumGenerationCreditCost || 0);
          const creditCost = current.generationCreditCost != null ? current.generationCreditCost : record.generationCreditCost;
          const isBelowMinimum = minimumCreditCost > 0 && Number(creditCost) < minimumCreditCost;
          return (
            <Button
              type="primary"
              onClick={() => this.handleSaveGenerationModel(modelKey)}
              loading={isSaving}
              disabled={isBelowMinimum}
            >
              Save
            </Button>
          );
        },
      },
    ];

    const components = {
      body: {
        wrapper: (wrapperProps) => (
          <SortableBody
            useDragHandle
            helperClass="admin-products__dragging-row"
            onSortEnd={this.handleGenerationModelsSortEnd}
            lockAxis="y"
            distance={6}
            {...wrapperProps}
          />
        ),
        row: (rowProps) => {
          const rowKey = rowProps['data-row-key'];
          const index = generationModels.findIndex((item) => (item.modelKey || item.id) === rowKey);
          return <SortableRow index={index < 0 ? 0 : index} {...rowProps} />;
        },
      },
    };

    return (
      <Table
        rowKey="modelKey"
        columns={columns}
        dataSource={generationModels}
        pagination={false}
        loading={generationModelsLoading || generationModelsOrderSaving}
        components={components}
        className="admin-products__model-table"
        tableLayout="fixed"
        scroll={{ x: 900 }}
      />
    );
  }

  handleFeatureInputChange = (event) => {
    const { value } = event.target;
    this.handleInputChange('tempFeature', value);
  };

  addFeature = () => {
    this.setState((prevState) => {
      const trimmed = (prevState.formValues.tempFeature || '').trim();
      if (!trimmed) {
        return null;
      }
      const nextFeatures = [...(prevState.formValues.features || []), trimmed];
      return {
        formValues: {
          ...prevState.formValues,
          features: nextFeatures,
          tempFeature: '',
        },
      };
    });
  };

  removeFeature = (index) => {
    this.setState((prevState) => {
      const nextFeatures = [...(prevState.formValues.features || [])];
      nextFeatures.splice(index, 1);
      return {
        formValues: {
          ...prevState.formValues,
          features: nextFeatures,
        },
      };
    });
  };

  handleSave = async () => {
    const { editing, formValues } = this.state;
    const trimmedDescription = (formValues.description || '').trim();
    const meaningfulFeatures = (formValues.features || [])
      .map((feature) => feature.trim())
      .filter((feature) => feature);
    const landingTitle = (formValues.landingTitle || '').trim();
    const landingPayload = {
      title: landingTitle || null,
      tagline: (formValues.landingTagline || '').trim() || null,
      description: (formValues.landingDescription || '').trim() || null,
      priceText: (formValues.landingPriceText || '').trim() || null,
      billingText: (formValues.landingBillingText || '').trim() || null,
      saveText: (formValues.landingSaveText || '').trim() || null,
      badge: (formValues.landingBadge || '').trim() || null,
      bestFor: (formValues.landingBestFor || '').trim() || null,
      theme: formValues.landingTheme || 'lilac',
      buttonLabel: (formValues.landingButtonLabel || '').trim() || null,
      creditsPerYear:
        formValues.landingCreditsPerYear != null && formValues.landingCreditsPerYear !== ''
          ? Number(formValues.landingCreditsPerYear)
          : 0,
      bonusCredits:
        formValues.landingBonusCredits != null && formValues.landingBonusCredits !== ''
          ? Number(formValues.landingBonusCredits)
          : 0,
      displayOrder:
        formValues.landingDisplayOrder != null && formValues.landingDisplayOrder !== ''
          ? Number(formValues.landingDisplayOrder)
          : 0,
    };

    if (!trimmedDescription) {
      message.warning('Description is required.');
      return;
    }
    if (formValues.price == null || Number(formValues.price) < 0) {
      message.warning('Enter a valid price.');
      return;
    }
    if (!formValues.period || Number(formValues.period) <= 0) {
      message.warning('Enter a valid billing period.');
      return;
    }
    if (!formValues.periodUnit) {
      message.warning('Select a billing period unit.');
      return;
    }
    if (formValues.generationLimit == null || Number(formValues.generationLimit) < 0) {
      message.warning('Enter a valid generation limit.');
      return;
    }

    this.setState({ saving: true });
    try {
      if (editing) {
        const editingId = editing._id || editing.id;
        await adminApi.updateProduct(editingId, {
          description: trimmedDescription,
          features: meaningfulFeatures,
          price: Number(formValues.price),
          period: Number(formValues.period),
          periodUnit: formValues.periodUnit,
          generationLimit: Number(formValues.generationLimit),
          isActive: formValues.isActive,
          isPublic: Boolean(formValues.isPublic),
          landing: landingPayload,
        });
        message.success('Product updated.');
      } else {
        await adminApi.createProduct({
          description: trimmedDescription,
          features: meaningfulFeatures,
          price: Number(formValues.price),
          period: Number(formValues.period),
          periodUnit: formValues.periodUnit,
          generationLimit: Number(formValues.generationLimit),
          isPublic: Boolean(formValues.isPublic),
          landing: landingPayload,
        });
        message.success('Product created.');
      }
      this.closeModal();
      this.fetchProducts();
    } catch (error) {
      const errMessage = (error && error.response && error.response.data && error.response.data.message)
        ? error.response.data.message
        : 'Failed to save product.';
      message.error(errMessage);
      this.setState({ saving: false });
    }
  };

  getMonthlyPriceDisplay = (product) => {
    const priceSource =
      product && product.price !== undefined && product.price !== null
        ? product.price
        : 0;

    const periodSource =
      product && product.period !== undefined && product.period !== null
        ? product.period
        : 1;

    const unitSource = (product && product.periodUnit) || 'month';

    return formatMonthlyPrice(priceSource, periodSource, unitSource);
  };

  handleTogglePublic = async (product, isPublic) => {
    const productId = product && (product._id || product.id);
    if (!productId) {
      message.warning('Unable to determine product identifier.');
      return;
    }
    try {
      await adminApi.updateProduct(productId, { isPublic });
      this.setState((prevState) => ({
        products: prevState.products.map((item) => (
          (item._id || item.id) === productId
            ? { ...item, isPublic }
            : item
        )),
      }));
      message.success(isPublic ? 'Plan pushed to users.' : 'Plan removed from user view.');
    } catch (error) {
      const errMessage = (error && error.response && error.response.data && error.response.data.message)
        ? error.response.data.message
        : 'Unable to update public plan state.';
      message.error(errMessage);
    }
  };

  handleDeleteProduct = async (product) => {
    const productId = product && (product._id || product.id);
    if (!productId) {
      message.warning('Unable to determine product identifier.');
      return;
    }

    const isTrialProduct = this.isTrialProduct(product);
    const subscriptionsMap = refreshSubscriptions();
    const inUse = Object.values(subscriptionsMap || {}).some((subscription) => {
      if (!subscription) {
        return false;
      }
      if (subscription.productId && subscription.productId === productId) {
        return true;
      }
      if (subscription.planId && (subscription.planId === productId || subscription.planId === product.planId)) {
        return true;
      }
      return false;
    });

    if (inUse && !isTrialProduct) {
      message.warning('This plan is currently assigned to a customer and cannot be deleted.');
      return;
    }

    const confirmed = window.confirm(
      `Delete "${product.description || 'this plan'}"? This will remove it for users as well.`,
    );
    if (!confirmed) {
      return;
    }

    try {
      await adminApi.updateProduct(productId, { isActive: false });
    } catch (error) {
      const errMessage = (error && error.response && error.response.data && error.response.data.message)
        ? error.response.data.message
        : 'Unable to delete the plan';
      message.error(errMessage);
      return;
    }

    this.setState((prevState) => ({
      products: prevState.products.filter((item) => (item._id || item.id) !== productId),
    }));
    message.success('Plan removed.');
  };

  isTrialProduct(product) {
    if (!product) {
      return false;
    }
    const description = String(product.description || '').toLowerCase();
    const planId = String(product.planId || product._id || product.id || '').toLowerCase();
    const price = Number(product.price || 0);
    return (
      description.includes('trial') ||
      planId === 'trial' ||
      (description.includes('starter') && price === 0)
    );
  }

  renderPlanCard = (product) => {
    const landing = product && product.landing ? product.landing : {};
    const title = landing.title || product.description || 'Plan';
    const price = landing.priceText || this.getMonthlyPriceDisplay(product);
    const features =
      (product && Array.isArray(product.features) && product.features.length)
        ? product.features
        : [(landing.description || '').trim()].filter(Boolean);

    return (
      <div className="admin-products__plan-card">
        <div className="admin-products__plan-card-header">
          <span className="admin-products__plan-card-name">{title}</span>
          <span className="admin-products__plan-card-price">{price}</span>
        </div>
        {features.length > 0 && (
          <ul className="admin-products__plan-card-features">
            {features.map((feature) => (
              <li key={feature}>{feature}</li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  renderGenerationModelCard = (model, draft) => {
    const modelKey = model.modelKey || model.id;
    const isSaving = Boolean(this.state.generationModelsSaving[modelKey]);
    const current = draft || {};
    const providerLabel = current.provider || model.provider || 'unknown';
    const creditCost = current.generationCreditCost != null ? current.generationCreditCost : model.generationCreditCost;
    const providerCostUsd = current.providerCostUsd != null ? current.providerCostUsd : model.providerCostUsd;
    const referenceSupport = current.supportsReferenceImages != null
      ? current.supportsReferenceImages
      : model.supportsReferenceImages;
    const category = current.category || model.category || 'generate';
    const enabled = current.isEnabled != null ? current.isEnabled : model.isEnabled;

    return (
      <div className="admin-products__model-card">
        <div className="admin-products__model-card-header">
          <div>
            <span className="admin-products__plan-card-name">{current.label || model.label}</span>
            <p className="admin-products__model-card-meta">
              {providerLabel} · {model.providerModel}
            </p>
          </div>
          <Button type="primary" onClick={() => this.handleSaveGenerationModel(modelKey)} loading={isSaving}>
            Save
          </Button>
        </div>
        <div className="admin-products__model-grid">
          <label>
            Credit cost / image
            <InputNumber
              min={0}
              precision={0}
              style={{ width: '100%' }}
              value={creditCost}
              onChange={(value) => this.handleGenerationModelInputChange(modelKey, 'generationCreditCost', value)}
            />
          </label>
          <label>
            Provider cost (USD)
            <InputNumber
              min={0}
              precision={3}
              step={0.001}
              style={{ width: '100%' }}
              value={providerCostUsd}
              onChange={(value) => this.handleGenerationModelInputChange(modelKey, 'providerCostUsd', value)}
            />
          </label>
          <label>
            Sort order
            <InputNumber
              min={0}
              precision={0}
              style={{ width: '100%' }}
              value={current.sortOrder != null ? current.sortOrder : model.sortOrder}
              onChange={(value) => this.handleGenerationModelInputChange(modelKey, 'sortOrder', value)}
            />
          </label>
          <label>
            Category
            <Select
              value={category}
              onChange={(value) => this.handleGenerationModelInputChange(modelKey, 'category', value)}
            >
              <Select.Option value="generate">Generate</Select.Option>
              <Select.Option value="edit">Edit</Select.Option>
            </Select>
          </label>
        </div>
        <div className="admin-products__switch-row">
          <div className="admin-products__switch">
            <span>Enabled</span>
            <Switch
              checked={enabled}
              onChange={(value) => this.handleGenerationModelInputChange(modelKey, 'isEnabled', value)}
            />
          </div>
          <div className="admin-products__switch">
            <span>Reference images</span>
            <Switch
              checked={referenceSupport}
              onChange={(value) => this.handleGenerationModelInputChange(modelKey, 'supportsReferenceImages', value)}
            />
          </div>
        </div>
        <p className="admin-products__model-note">
          Suggested cost: {creditCost != null ? `${creditCost} credit${Number(creditCost) === 1 ? '' : 's'} per image` : '—'}
        </p>
      </div>
    );
  };

  render() {
    const {
      products,
      loading,
      generationModels,
      generationModelsLoading,
      modalVisible,
      editing,
      formValues,
      saving,
    } = this.state;
    const { admin, onLogout } = this.props;
    const isViewer = admin && admin.user && admin.user.type === 'viewer';

    if (isViewer) {
      const planColumns = [
        { title: 'Plan', key: 'plan', render: (_, record) => this.renderPlanCard(record) },
        { title: 'Monthly price', key: 'price', render: (_, record) => this.getMonthlyPriceDisplay(record) },
        { title: 'Credits', dataIndex: 'generationLimit', key: 'credits', render: (value) => Number(value || 0).toLocaleString() },
        { title: 'Active', dataIndex: 'isActive', key: 'active', render: (value) => (value ? 'Yes' : 'No') },
        { title: 'Public', dataIndex: 'isPublic', key: 'public', render: (value) => (value ? 'Yes' : 'No') },
      ];
      const modelColumns = [
        { title: 'Model', dataIndex: 'label', key: 'label' },
        { title: 'Provider', dataIndex: 'provider', key: 'provider' },
        { title: 'Credit cost / image', dataIndex: 'generationCreditCost', key: 'cost' },
        { title: 'Enabled', dataIndex: 'isEnabled', key: 'enabled', render: (value) => (value ? 'Yes' : 'No') },
      ];
      return (
        <AdminLayout
          title="Products"
          subtitle="View credit plans, pricing, and generation models."
          activeKey="products"
          admin={admin}
          onLogout={onLogout}
        >
          <Table rowKey="_id" columns={planColumns} dataSource={products} loading={loading} pagination={false} />
          <div className="admin-products__section">
            <h2>Generation Models</h2>
            <Table
              rowKey="modelKey"
              columns={modelColumns}
              dataSource={generationModels}
              loading={generationModelsLoading}
              pagination={false}
            />
          </div>
        </AdminLayout>
      );
    }

    const columns = [
      {
        title: 'Plan',
        key: 'plan',
        render: (value, record) => this.renderPlanCard(record),
      },
      {
        title: 'Monthly price',
        dataIndex: 'price',
        key: 'price',
        render: (value, record) => this.getMonthlyPriceDisplay(record),
      },
      {
        title: 'Period',
        dataIndex: 'period',
        key: 'period',
        render: (value, record) => {
          if (value == null) {
            return '—';
          }
          const unit = record.periodUnit || 'day';
          const suffix = Number(value) === 1 ? '' : 's';
          return `${value} ${unit}${suffix}`;
        },
      },
      {
        title: 'Generation limit',
        dataIndex: 'generationLimit',
        key: 'generationLimit',
        render: (value) => (value != null ? value.toLocaleString() : '—'),
      },
      {
        title: 'Active',
        dataIndex: 'isActive',
        key: 'active',
        render: (value) => (value ? 'Yes' : 'No'),
      },
      {
        title: 'Push to public',
        key: 'public',
        align: 'center',
        render: (value, record) => (
          <Switch
            checked={Boolean(record.isPublic)}
            onChange={(isChecked) => this.handleTogglePublic(record, isChecked)}
          />
        ),
      },
      {
            title: 'Actions',
            key: 'actions',
            render: (value, record) => (
              <div className="admin-products__actions">
                <Button type="link" onClick={() => this.openModal(record)}>
                  Edit
                </Button>
                <Button type="link" danger onClick={() => this.handleDeleteProduct(record)}>
                  Delete
                </Button>
              </div>
            ),
      },
    ];

    return (
      <AdminLayout
        title="Products"
        subtitle="Manage available credit plans and pricing."
        activeKey="products"
        admin={admin}
        onLogout={onLogout}
      >
        <div className="admin-products__toolbar">
          <Button type="primary" onClick={this.openCreateModal}>
            New plan
          </Button>
        </div>
        {loading ? (
          <div className="admin-products__loading">
            <Spin />
          </div>
        ) : (
          <Table rowKey="_id" columns={columns} dataSource={products} pagination={false} />
        )}

        <div className="admin-products__section">
          <div className="admin-products__section-header">
            <div>
              <h2>Generation Models</h2>
              <p>Set the credit cost per image and enable or disable models for the generator UI.</p>
            </div>
            <Button onClick={() => this.fetchGenerationModels()}>Refresh models</Button>
          </div>
          {generationModelsLoading ? (
            <div className="admin-products__loading">
              <Spin />
            </div>
          ) : (
            this.renderGenerationModelsTable()
          )}
        </div>

        <Modal
          visible={modalVisible}
          title={editing ? `Edit ${editing.description}` : 'Create plan'}
          onCancel={this.closeModal}
          onOk={this.handleSave}
          confirmLoading={saving}
          okText={editing ? 'Save changes' : 'Create plan'}
        >
          <div className="admin-products__form">
            <label htmlFor="productDescription">Plan name</label>
            <Input
              id="productDescription"
              placeholder="e.g. Growth plan"
              value={formValues.description}
              onChange={(event) => this.handleInputChange('description', event.target.value)}
            />

            <label htmlFor="productFeatureInput">Plan features</label>
            <div className="admin-products__feature-input">
              <Input
                id="productFeatureInput"
                placeholder="Add a feature and press Enter"
                value={formValues.tempFeature}
                onChange={this.handleFeatureInputChange}
                onPressEnter={(event) => {
                  event.preventDefault();
                  this.addFeature();
                }}
              />
              <Button type="default" onClick={this.addFeature} htmlType="button">
                Add feature
              </Button>
            </div>
            {Array.isArray(formValues.features) && formValues.features.length > 0 && (
              <ul className="admin-products__feature-list">
                {formValues.features.map((feature, index) => (
                  <li key={`${feature}-${index}`}>
                    <span>{feature}</span>
                    <Button
                      type="link"
                      danger
                      onClick={() => this.removeFeature(index)}
                    >
                      Delete
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            <label htmlFor="productPrice">Price ($)</label>
            <InputNumber
              id="productPrice"
              min={0}
              precision={2}
              style={{ width: '100%' }}
              value={formValues.price}
              onChange={(value) => this.handleInputChange('price', value)}
            />
            <label htmlFor="productPeriod">Period</label>
            <div className="admin-products__period-row">
              <InputNumber
                id="productPeriod"
                min={1}
                value={formValues.period}
                onChange={(value) => this.handleInputChange('period', value)}
              />
              <Select
                value={formValues.periodUnit}
                onChange={(value) => this.handleInputChange('periodUnit', value)}
              >
                <Select.Option value="day">Day(s)</Select.Option>
                <Select.Option value="month">Month(s)</Select.Option>
                <Select.Option value="year">Year(s)</Select.Option>
              </Select>
            </div>
            <label htmlFor="productGenerationLimit">Generation limit / total credits</label>
            <InputNumber
              id="productGenerationLimit"
              min={0}
              precision={0}
              style={{ width: '100%' }}
              value={formValues.generationLimit}
              onChange={(value) => this.handleInputChange('generationLimit', value)}
            />
            <label htmlFor="landingTitle">Landing title</label>
            <Input
              id="landingTitle"
              placeholder="e.g. Starter"
              value={formValues.landingTitle}
              onChange={(event) => this.handleInputChange('landingTitle', event.target.value)}
            />
            <label htmlFor="landingTagline">Landing tagline</label>
            <Input
              id="landingTagline"
              placeholder="e.g. Start small. Start now."
              value={formValues.landingTagline}
              onChange={(event) => this.handleInputChange('landingTagline', event.target.value)}
            />
            <label htmlFor="landingDescription">Landing description</label>
            <Input.TextArea
              id="landingDescription"
              rows={3}
              placeholder="Short landing-page plan description"
              value={formValues.landingDescription}
              onChange={(event) => this.handleInputChange('landingDescription', event.target.value)}
            />
            <label htmlFor="landingPriceText">Landing price text</label>
            <Input
              id="landingPriceText"
              placeholder="e.g. ฿2,490 / month"
              value={formValues.landingPriceText}
              onChange={(event) => this.handleInputChange('landingPriceText', event.target.value)}
            />
            <label htmlFor="landingBillingText">Landing billing text</label>
            <Input
              id="landingBillingText"
              placeholder="e.g. (Billed annually)"
              value={formValues.landingBillingText}
              onChange={(event) => this.handleInputChange('landingBillingText', event.target.value)}
            />
            <label htmlFor="landingSaveText">Landing save text</label>
            <Input
              id="landingSaveText"
              placeholder="e.g. SAVE ฿5,960 / month"
              value={formValues.landingSaveText}
              onChange={(event) => this.handleInputChange('landingSaveText', event.target.value)}
            />
            <label htmlFor="landingBadge">Landing badge</label>
            <Input
              id="landingBadge"
              placeholder="e.g. Most Popular"
              value={formValues.landingBadge}
              onChange={(event) => this.handleInputChange('landingBadge', event.target.value)}
            />
            <label htmlFor="landingBestFor">Landing best-for text</label>
            <Input.TextArea
              id="landingBestFor"
              rows={2}
              placeholder="e.g. creators who are getting serious"
              value={formValues.landingBestFor}
              onChange={(event) => this.handleInputChange('landingBestFor', event.target.value)}
            />
            <label htmlFor="landingButtonLabel">Landing button label</label>
            <Input
              id="landingButtonLabel"
              placeholder="e.g. Select Plan"
              value={formValues.landingButtonLabel}
              onChange={(event) => this.handleInputChange('landingButtonLabel', event.target.value)}
            />
            <label htmlFor="landingCreditsPerYear">Landing credits per year</label>
            <InputNumber
              id="landingCreditsPerYear"
              min={0}
              precision={0}
              style={{ width: '100%' }}
              value={formValues.landingCreditsPerYear}
              onChange={(value) => this.handleInputChange('landingCreditsPerYear', value)}
            />
            <label htmlFor="landingBonusCredits">Landing bonus credits</label>
            <InputNumber
              id="landingBonusCredits"
              min={0}
              precision={0}
              style={{ width: '100%' }}
              value={formValues.landingBonusCredits}
              onChange={(value) => this.handleInputChange('landingBonusCredits', value)}
            />
            <label htmlFor="landingTheme">Landing theme</label>
            <Select
              id="landingTheme"
              value={formValues.landingTheme}
              onChange={(value) => this.handleInputChange('landingTheme', value)}
            >
              <Select.Option value="lilac">Lilac</Select.Option>
              <Select.Option value="violet">Violet</Select.Option>
              <Select.Option value="orange">Orange</Select.Option>
            </Select>
            <label htmlFor="landingDisplayOrder">Landing display order</label>
            <InputNumber
              id="landingDisplayOrder"
              min={0}
              precision={0}
              style={{ width: '100%' }}
              value={formValues.landingDisplayOrder}
              onChange={(value) => this.handleInputChange('landingDisplayOrder', value)}
            />
            <div className="admin-products__switch">
              <span>Public landing plan</span>
              <Switch
                checked={formValues.isPublic}
                onChange={(value) => this.handleInputChange('isPublic', value)}
              />
            </div>
            {editing && (
              <div className="admin-products__switch">
                <span>Active</span>
                <Switch
                  checked={formValues.isActive}
                  onChange={(value) => this.handleInputChange('isActive', value)}
                />
              </div>
            )}
          </div>
        </Modal>
      </AdminLayout>
    );
  }
}

AdminProducts.propTypes = {
  admin: PropTypes.shape({
    user: PropTypes.object,
  }),
  onLogout: PropTypes.func.isRequired,
};

AdminProducts.defaultProps = {
  admin: null,
};

const mapStateToProps = (state) => ({
  admin: state.auth.admin,
});

const mapDispatchToProps = {
  onLogout: clearSession,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminProducts);
